import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { MessageCircle } from "lucide-react";
import { openLiveChat } from "../../lib/chat";
import { OLIVEUP_MAILTO } from "../../lib/contact";

const faqs = [
  {
    q: "What types of businesses do you work with?",
    a: "Pretty much all of them! Restaurants, retail stores, auto shops, salons, construction companies, online businesses, medical practices — if it's a legitimate small business, we can help. We've helped over 50 different industries get funded and counting.",
  },
  {
    q: "My credit isn't great. Can I still get approved?",
    a: "Your business is more than just a credit score. We specialize in holistic underwriting that looks at your annual revenue and growth potential. We’re here for the business owners who need a partner that values their future more than their financial past.",
  },
  {
    q: "How fast can I get my funds?",
    a: "Our record is 2 hours from application to funding (yes, really). Most clients receive funds within 24–48 hours after approval. SBA loans take a bit longer — typically 30–60 days — but that's just how the government rolls.",
  },
  {
    q: "What loan amounts are available?",
    a: "We offer access to funding from $5,000 all the way up to $20 million for commercial real estate. Most of our clients fall in the $25K–$500K range. The amount you qualify for depends on your business revenue, time in business, and the type of financing you need.",
  },
  {
    q: "Are there any upfront fees or hidden costs?",
    a: "Nope! We believe in transparency (and we're terrible at keeping secrets anyway). There are no application fees, and we clearly explain all costs before you sign anything. Our team will walk you through every number so you know exactly what you're getting into.",
  },
  {
    q: "Do I need collateral?",
    a: "It depends on the loan type. Equipment financing uses the equipment as collateral. SBA loans may require collateral for larger amounts. Commercial loans and lines of credit typically don't require collateral — we base approval on your business revenue. We'll find you the best option for your situation.",
  },
  {
    q: "How does the application process work?",
    a: "Super simple: 1) Fill out our 5-minute online application, 2) Upload a few basic documents (bank statements, business info), 3) Our team reviews your application and gets back to you within 2 hours, 4) Get approved and receive funds. No endless paperwork, no waiting weeks for an answer.",
  },
  {
    q: "Can I pay off my loan early?",
    a: "In most cases, yes! Many of our funding options have no prepayment penalties. We'll clearly explain the terms for your specific offer so you know exactly what to expect. We actually love when clients pay early — it means your business is thriving!",
  },
];

export default function FAQ() {
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-32"
    >
      <div className="container-tight grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#F5B82E]">
            Questions?
          </span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tight text-foreground leading-[1.05]">
            Got Questions? <br />We&apos;ve Got Answers.
          </h2>
          <p className="mt-5 text-base text-muted-foreground italic">
            (And olives. We always have olives.)
          </p>

          <div className="mt-10 rounded-3xl bg-secondary p-6 border border-border">
            <p className="font-display text-lg font-bold text-foreground">
              Still confused?
            </p>
            <p className="text-sm text-muted-foreground mt-1.5">
              We don&apos;t bite (unlike real olives).
            </p>
            <button
              type="button"
              onClick={openLiveChat}
              data-testid="faq-chat-cta"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary hover:bg-[#3B4A1C] text-primary-foreground px-5 py-3 text-sm font-semibold transition-all hover:-translate-y-0.5"
            >
              <MessageCircle className="h-4 w-4" />
              Live chat
            </button>
            <a
              href={OLIVEUP_MAILTO}
              data-testid="faq-email-cta"
              className="mt-3 block w-full text-center text-sm font-semibold text-primary hover:underline"
            >
              Or email us — we read everything
            </a>
          </div>
        </div>
        <div className="lg:col-span-8">
          <Accordion
            type="single"
            collapsible
            className="space-y-3"
            data-testid="faq-accordion"
          >
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                className="bg-card border border-border rounded-2xl px-6 data-[state=open]:shadow-[0_8px_30px_rgb(0,0,0,0.05)]"
              >
                <AccordionTrigger className="font-display text-lg md:text-xl font-semibold text-foreground hover:no-underline py-5 text-left">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-base text-muted-foreground leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
